"use client";

import * as React from "react";

import { useProducts, type UseProductsOptions } from "@/hooks/product";
import type { FetchApiError, StrapiCollectionResponse, StrapiEntity } from "@/lib/strapi";
import type { ProductAttributes } from "@/services/product";

export type ProductsStoreValue = {
  products: Array<StrapiEntity<ProductAttributes>>;
  data: StrapiCollectionResponse<ProductAttributes> | null;
  error: FetchApiError | null;
  isLoading: boolean;
  refetch: () => void;
};

const ProductsStoreContext = React.createContext<ProductsStoreValue | null>(null);

export function ProductsProvider({
  children,
  initialData,
  ...options
}: React.PropsWithChildren<UseProductsOptions & { initialData?: StrapiCollectionResponse<ProductAttributes> | null }>): React.JSX.Element {
  const state = useProducts({ ...options, initialData });

  const value: ProductsStoreValue = {
    products: state.products,
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    refetch: state.refetch,
  };

  return <ProductsStoreContext.Provider value={value}>{children}</ProductsStoreContext.Provider>;
}

export function useProductsStore(): ProductsStoreValue {
  const value = React.useContext(ProductsStoreContext);
  if (!value) {
    throw new Error("useProductsStore must be used within ProductsProvider");
  }
  return value;
}
